/**
 * Offline Cache — IndexedDB-based local storage for chat and journal data
 * Caches recent conversations and journal entries for offline access.
 * Queues writes made offline and syncs when back online.
 *
 * AI-4713: Offline caching for chat history and journal entries
 */

import { openDB, type IDBPDatabase } from 'idb';

// ============================================
// TYPES
// ============================================

export interface CachedConversation {
  id: string;
  mode: string;
  messages: CachedMessage[];
  updatedAt: number;
  synced: boolean;
}

export interface CachedMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export interface CachedJournalEntry {
  id: string;
  type: 'soap' | 'prayer' | 'gratitude' | 'freeform';
  title: string | null;
  content: string | null;
  scripture: string | null;
  observation: string | null;
  application: string | null;
  prayer: string | null;
  gratitudeItems: string[] | null;
  prayerCategory: string | null;
  mood: string | null;
  createdAt: number;
  updatedAt: number;
  synced: boolean;
}

export interface PendingSync {
  id: string;
  type: 'journal_create' | 'journal_update';
  payload: Record<string, unknown>;
  createdAt: number;
}

// ============================================
// DATABASE
// ============================================

const DB_NAME = 'jetc-offline-cache';
const DB_VERSION = 1;
const CONVERSATIONS_STORE = 'conversations';
const JOURNAL_STORE = 'journal';
const SYNC_QUEUE_STORE = 'sync_queue';

let dbInstance: IDBPDatabase | null = null;

async function getDB(): Promise<IDBPDatabase> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db: IDBPDatabase) {
      if (!db.objectStoreNames.contains(CONVERSATIONS_STORE)) {
        const convStore = db.createObjectStore(CONVERSATIONS_STORE, { keyPath: 'id' });
        convStore.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains(JOURNAL_STORE)) {
        const journalStore = db.createObjectStore(JOURNAL_STORE, { keyPath: 'id' });
        journalStore.createIndex('createdAt', 'createdAt');
        journalStore.createIndex('type', 'type');
      }
      if (!db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
        db.createObjectStore(SYNC_QUEUE_STORE, { keyPath: 'id' });
      }
    },
  });

  return dbInstance;
}

// ============================================
// CONVERSATION CACHE
// ============================================

/**
 * Save a conversation to the local cache.
 */
export async function cacheConversation(conversation: CachedConversation): Promise<void> {
  try {
    const db = await getDB();
    await db.put(CONVERSATIONS_STORE, conversation);
  } catch (err) {
    console.error('Failed to cache conversation:', err);
  }
}

/**
 * Get a cached conversation by ID.
 */
export async function getCachedConversation(id: string): Promise<CachedConversation | null> {
  try {
    const db = await getDB();
    const result = await db.get(CONVERSATIONS_STORE, id);
    return result || null;
  } catch {
    return null;
  }
}

/**
 * Get all cached conversations, ordered by most recent.
 */
export async function getAllCachedConversations(): Promise<CachedConversation[]> {
  try {
    const db = await getDB();
    const all = await db.getAll(CONVERSATIONS_STORE);
    return (all as CachedConversation[]).sort((a: CachedConversation, b: CachedConversation) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

/**
 * Cache messages for the current chat session.
 * Called after each message exchange so they persist if app goes offline.
 */
export async function cacheChatMessages(
  conversationId: string,
  mode: string,
  messages: Array<{ id: string; role: 'user' | 'assistant'; content: string; timestamp: Date }>
): Promise<void> {
  const cached: CachedConversation = {
    id: conversationId,
    mode,
    messages: messages.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      timestamp: m.timestamp.getTime(),
    })),
    updatedAt: Date.now(),
    synced: true,
  };
  await cacheConversation(cached);
}

// ============================================
// JOURNAL CACHE
// ============================================

/**
 * Cache a journal entry locally.
 */
export async function cacheJournalEntry(entry: CachedJournalEntry): Promise<void> {
  try {
    const db = await getDB();
    await db.put(JOURNAL_STORE, entry);
  } catch (err) {
    console.error('Failed to cache journal entry:', err);
  }
}

/**
 * Get all cached journal entries, most recent first.
 */
export async function getCachedJournalEntries(
  type?: string
): Promise<CachedJournalEntry[]> {
  try {
    const db = await getDB();
    let all: CachedJournalEntry[];

    if (type) {
      all = await db.getAllFromIndex(JOURNAL_STORE, 'type', type);
    } else {
      all = await db.getAll(JOURNAL_STORE);
    }

    return all.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

/**
 * Get a single cached journal entry.
 */
export async function getCachedJournalEntry(id: string): Promise<CachedJournalEntry | null> {
  try {
    const db = await getDB();
    const result = await db.get(JOURNAL_STORE, id);
    return result || null;
  } catch {
    return null;
  }
}

/**
 * Save a journal entry for offline sync.
 * Creates the entry in local cache and adds to sync queue.
 */
export async function saveJournalOffline(
  entry: Omit<CachedJournalEntry, 'synced'>
): Promise<void> {
  await cacheJournalEntry({ ...entry, synced: false });
  await addToSyncQueue({
    id: `sync-${entry.id}-${Date.now()}`,
    type: 'journal_create',
    payload: entry as unknown as Record<string, unknown>,
    createdAt: Date.now(),
  });
}

// ============================================
// SYNC QUEUE
// ============================================

/**
 * Add an item to the offline sync queue.
 */
export async function addToSyncQueue(item: PendingSync): Promise<void> {
  try {
    const db = await getDB();
    await db.put(SYNC_QUEUE_STORE, item);
  } catch (err) {
    console.error('Failed to add to sync queue:', err);
  }
}

/**
 * Get all pending sync items.
 */
export async function getPendingSyncItems(): Promise<PendingSync[]> {
  try {
    const db = await getDB();
    return await db.getAll(SYNC_QUEUE_STORE);
  } catch {
    return [];
  }
}

/**
 * Remove a sync item after successful sync.
 */
export async function removeSyncItem(id: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete(SYNC_QUEUE_STORE, id);
  } catch (err) {
    console.error('Failed to remove sync item:', err);
  }
}

/**
 * Mark a journal entry as synced.
 */
export async function markJournalSynced(id: string): Promise<void> {
  try {
    const db = await getDB();
    const entry = await db.get(JOURNAL_STORE, id);
    if (entry) {
      entry.synced = true;
      await db.put(JOURNAL_STORE, entry);
    }
  } catch (err) {
    console.error('Failed to mark journal as synced:', err);
  }
}

/**
 * Sync all pending items when back online.
 */
export async function syncPendingItems(
  apiUrl: string,
  getAuthToken: () => Promise<string | null>
): Promise<{ synced: number; failed: number }> {
  const pending = await getPendingSyncItems();
  let synced = 0;
  let failed = 0;

  for (const item of pending) {
    try {
      const token = await getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let endpoint = '';
      let method = 'POST';

      switch (item.type) {
        case 'journal_create':
          endpoint = `${apiUrl}/journal`;
          method = 'POST';
          break;
        case 'journal_update':
          endpoint = `${apiUrl}/journal/${item.payload.id}`;
          method = 'PUT';
          break;
      }

      if (endpoint) {
        const res = await fetch(endpoint, {
          method,
          headers,
          body: JSON.stringify(item.payload),
        });

        if (res.ok) {
          await removeSyncItem(item.id);
          if (item.payload.id) {
            await markJournalSynced(item.payload.id as string);
          }
          synced++;
        } else {
          failed++;
        }
      }
    } catch {
      failed++;
    }
  }

  return { synced, failed };
}

/**
 * Cache multiple journal entries from an API response.
 */
export async function cacheJournalEntriesFromAPI(
  entries: Array<{
    id: string;
    type: string;
    title?: string | null;
    content?: string | null;
    scripture?: string | null;
    observation?: string | null;
    application?: string | null;
    prayer?: string | null;
    gratitudeItems?: string[] | null;
    prayerCategory?: string | null;
    mood?: string | null;
    createdAt: string;
    updatedAt?: string;
  }>
): Promise<void> {
  for (const entry of entries) {
    await cacheJournalEntry({
      id: entry.id,
      type: entry.type as CachedJournalEntry['type'],
      title: entry.title || null,
      content: entry.content || null,
      scripture: entry.scripture || null,
      observation: entry.observation || null,
      application: entry.application || null,
      prayer: entry.prayer || null,
      gratitudeItems: entry.gratitudeItems || null,
      prayerCategory: entry.prayerCategory || null,
      mood: entry.mood || null,
      createdAt: new Date(entry.createdAt).getTime(),
      updatedAt: entry.updatedAt ? new Date(entry.updatedAt).getTime() : Date.now(),
      synced: true,
    });
  }
}
